import { Button, IMenuItemProps, Position, Tooltip } from '@blueprintjs/core';
import React, { CSSProperties } from 'react';
import {
  blackpoint,
  blackpointEconomic,
  blackpointHealthy,
  blackpointSpecial,
  blackpointInactive,
  dutyDay,
  dutyDayNight,
  iconE,
  dutyOff
} from '../../assets';
import { TLCode } from '../../common/enums/TLCode';
import { MultiCalendarSelectionType } from './MultiCalendar';
import { DivColumn, DivLine, imageButtonStyle, Title } from './Toolbox';
import TranslatedMessage from './translation/TranslatedMessage';
import { WithTranslateProps } from './WithTranslate';

interface ToolSelectionTypeButtonProps {
  content: string;
  currentSelectionType: MultiCalendarSelectionType | undefined;
  buttonType: MultiCalendarSelectionType;
  onButtonClicked: () => void;
  imgSrc: any;
  isDisabled?: boolean;
  withLabel?: boolean | undefined;
  id?: string;
}

interface AllToolPartsProps extends WithTranslateProps {
  selectedType: MultiCalendarSelectionType | undefined;
  onTypeClicked: (type: MultiCalendarSelectionType) => void;
  blackPointHolidayCount?: number;
  blackPointHolidayMax?: number;
  isSecretariat?: boolean;
  withLabel?: boolean | undefined;
  id?: string;
}

interface ToolboxToolPartProps extends WithTranslateProps {
  selectedType: MultiCalendarSelectionType | undefined;
  onTypeClicked: (type: MultiCalendarSelectionType) => void;
  blackPointHolidayCount?: number;
  blackPointHolidayMax?: number;
  isSecretariat?: boolean;
  style?: CSSProperties | undefined;
  hideTitle?: boolean | undefined;
  isVertical?: boolean | undefined;
  withLable?: boolean | undefined;
  id?: string;
}

const ToolSelectionTypeButton = ({
  content,
  currentSelectionType,
  buttonType,
  onButtonClicked,
  imgSrc,
  isDisabled,
  withLabel,
  id
}: ToolSelectionTypeButtonProps): JSX.Element => {
  const button = (
    <Button
      className={imageButtonStyle.toString()}
      active={currentSelectionType === buttonType}
      onClick={onButtonClicked}
      disabled={isDisabled}
      style={
        currentSelectionType === buttonType
          ? {
              borderBottomStyle: 'solid',
              borderBottomColor: 'red',
            }
          : undefined
      }
    >
      <img src={imgSrc} />
    </Button>
  );
  //TODO add label
  return withLabel !== undefined && withLabel ? (
    <DivLine>
      {button} <span>{content}</span>
    </DivLine>
  ) : (
    <Tooltip content={content} position={Position.BOTTOM}>
      {button}
    </Tooltip>
  );
};

const AllToolParts = ({
  translate,
  selectedType,
  onTypeClicked,
  blackPointHolidayCount,
  blackPointHolidayMax,
  isSecretariat,
  withLabel,
  id
}: AllToolPartsProps) => {
  return (
    <>
      <ToolSelectionTypeButton
      id={'bpHoliday'}
        content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointHoliday)}
        currentSelectionType={selectedType}
        buttonType={MultiCalendarSelectionType.blackpoint}
        onButtonClicked={() => onTypeClicked(MultiCalendarSelectionType.blackpoint)}
        imgSrc={blackpoint}
        isDisabled={
          blackPointHolidayMax != null &&
          blackPointHolidayCount != null &&
          blackPointHolidayMax - blackPointHolidayCount <= 0
        }
        withLabel={withLabel}
      />
      <ToolSelectionTypeButton
      id={'bpEconomic'}
      content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointEconomic)}
        currentSelectionType={selectedType}
        buttonType={MultiCalendarSelectionType.blackpointEconomic}
        onButtonClicked={() => onTypeClicked(MultiCalendarSelectionType.blackpointEconomic)}
        imgSrc={blackpointEconomic}
        withLabel={withLabel}
      />
      <ToolSelectionTypeButton
      id={'bpHealth'}
        content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointSick)}
        currentSelectionType={selectedType}
        buttonType={MultiCalendarSelectionType.blackpointHealthy}
        onButtonClicked={() => onTypeClicked(MultiCalendarSelectionType.blackpointHealthy)}
        imgSrc={blackpointHealthy}
        withLabel={withLabel}
      />
      {isSecretariat && (
        <>
          <ToolSelectionTypeButton
      id={'dayDuty'}
      content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDay)}
            currentSelectionType={selectedType}
            buttonType={MultiCalendarSelectionType.dayDuty}
            onButtonClicked={() => onTypeClicked(MultiCalendarSelectionType.dayDuty)}
            imgSrc={dutyDay}
            isDisabled={!isSecretariat}
            withLabel={withLabel}
          />
          <ToolSelectionTypeButton
      id={'dayNightDuty'}
      content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDayNight)}
            currentSelectionType={selectedType}
            buttonType={MultiCalendarSelectionType.dayNightDuty}
            onButtonClicked={() => onTypeClicked(MultiCalendarSelectionType.dayNightDuty)}
            imgSrc={dutyDayNight}
            isDisabled={!isSecretariat}
            withLabel={withLabel}
          />
          <ToolSelectionTypeButton
      id={'bpSpecial'}
      content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointSpecial)}
            currentSelectionType={selectedType}
            buttonType={MultiCalendarSelectionType.blackpointSpecial}
            onButtonClicked={() => onTypeClicked(MultiCalendarSelectionType.blackpointSpecial)}
            imgSrc={blackpointSpecial}
            withLabel={withLabel}
          />
          <ToolSelectionTypeButton
      id={'deactivate'}
      content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointSpecialDeactivate)}
            currentSelectionType={selectedType}
            buttonType={MultiCalendarSelectionType.inactive}
            onButtonClicked={() => onTypeClicked(MultiCalendarSelectionType.inactive)}
            imgSrc={blackpointSpecial}
            withLabel={withLabel}
          />
        </>
      )}
      <ToolSelectionTypeButton
      id={'delete'}
      content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_Erase)}
        currentSelectionType={selectedType}
        buttonType={MultiCalendarSelectionType.delete}
        onButtonClicked={() => onTypeClicked(MultiCalendarSelectionType.delete)}
        imgSrc={iconE}
        withLabel={withLabel}
      />
    </>
  );
};

export const ToolboxToolPart = (props: ToolboxToolPartProps) => {
  const allToolsParts = (
    <AllToolParts
      translate={props.translate}
      selectedType={props.selectedType}
      onTypeClicked={props.onTypeClicked}
      blackPointHolidayCount={props.blackPointHolidayCount}
      blackPointHolidayMax={props.blackPointHolidayMax}
      isSecretariat={props.isSecretariat}
      withLabel={props.isVertical}
    />
  );
  return (
    <DivColumn style={props.style}>
      {props.hideTitle !== undefined && props.hideTitle ? (
        <></>
      ) : (
        <DivLine>
          <Title>
            <TranslatedMessage id={TLCode.MULTI_CALENDAR_Toolbox} />
          </Title>
        </DivLine>
      )}
      <DivLine>
        <ToolSelectionTypeButton
        id={'bpHoliday2'}
          content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointHoliday)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.blackpoint}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.blackpoint)}
          imgSrc={blackpoint}
          isDisabled={
            props.blackPointHolidayMax != null &&
            props.blackPointHolidayCount != null &&
            props.blackPointHolidayMax - props.blackPointHolidayCount <= 0
          }
        />
        <ToolSelectionTypeButton
        id={'bpEconomic2'}
        content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointEconomic)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.blackpointEconomic}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.blackpointEconomic)}
          imgSrc={blackpointEconomic}
        />
        <ToolSelectionTypeButton
        id={'bpHealth2'}
        content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointSick)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.blackpointHealthy}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.blackpointHealthy)}
          imgSrc={blackpointHealthy}
        />
        {props.isSecretariat && (
          <>
            <ToolSelectionTypeButton
        id={'dayDuty2'}
        content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDay)}
              currentSelectionType={props.selectedType}
              buttonType={MultiCalendarSelectionType.dayDuty}
              onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.dayDuty)}
              imgSrc={dutyDay}
              isDisabled={!props.isSecretariat}
            />
            <ToolSelectionTypeButton
            id={'dayNightDuty2'}
              content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDayNight)}
              currentSelectionType={props.selectedType}
              buttonType={MultiCalendarSelectionType.dayNightDuty}
              onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.dayNightDuty)}
              imgSrc={dutyDayNight}
              isDisabled={!props.isSecretariat}
            />
            <ToolSelectionTypeButton
            id={'bpSpecial2'}
            content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointSpecial)}
              currentSelectionType={props.selectedType}
              buttonType={MultiCalendarSelectionType.blackpointSpecial}
              onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.blackpointSpecial)}
              imgSrc={blackpointSpecial}
            />
          </>
        )}
        <ToolSelectionTypeButton
            id={'delete2'}
            content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_Erase)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.delete}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.delete)}
          imgSrc={iconE}
        />
        <ToolSelectionTypeButton
            id={'deactivate2'}
            content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_Inactive)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.inactive}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.inactive)}
          imgSrc={blackpointInactive}
        />
      </DivLine>
{/* //       {props.isVertical !== undefined && props.isVertical ? allToolsParts : <DivLine>{allToolsParts}</DivLine>} */}
    </DivColumn>
  );
};
